import React, { useState } from "react";
import CheckBox from "@/app/components/ui/CheckBoxes/FormCheckbox";
import Input from "@/app/components/ui/Inputs/Input";
import AgeSelectMenu from "@/app/components/ui/SelectMenus/AgeSelectMenu";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import { useLoader } from "@/app/hooks/useLoader";
import { useUtils } from "@/app/hooks/useUtils";
import { IPetInfoFormProps } from "../../customer_profile_interface";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useBreedConstants } from "@/app/hooks/useBreedConstants";

const PetInfoFormComponent: React.FC<IPetInfoFormProps> = ({ formHook,hideTitle }) => {
  const { convertYDMtoDOBformat, convertToValidDateStringFormat, convertDOBToYMDFormat, getCurrentDate } = useUtils();
  const isCustomerCreationInProgress = useLoader("isCustomerCreationInProgress");
  const isCustomerPetCreationInProgress = useLoader("isCustomerPetCreationInProgress");
  const {
    petGenders: genderItems,
    petMoods,
    pet_species: speciesItems,
  } = useArrayConstants();

  const { catBreeds, dogBreeds, giantBreeds } = useBreedConstants();

  const initialPetSpeciesTypeId =
    formHook.getValues("pet_species_type_id") !== "" ? formHook.getValues("pet_species_type_id") : "1";
  const [_selectedSpecies, setSelectedSpecies] = useState<string>(initialPetSpeciesTypeId);
  const { forOnlyStrictAlphabets, forOnlyNumber } = useFormValidations();

  const catBreedItems: ISelectMenuItemData[] = useSelectMenuReducer(catBreeds, "pet_breed", "id");
  const dogBreedItems: ISelectMenuItemData[] = useSelectMenuReducer(dogBreeds, "pet_breed", "id");
  const giantBreedItems: ISelectMenuItemData[] = useSelectMenuReducer(giantBreeds, "pet_breed", "id");

  const dayWatch = formHook.watch("day");
  const monthWatch = formHook.watch("month");
  const yearWatch = formHook.watch("year");
  const breedIdWatch = formHook.watch("pet_breed_id");
  const speciesIdWatch = formHook.watch("pet_species_type_id");
  const genderWatch = formHook.watch("gender");
  const petMoodWatch = formHook.watch("pet_mood");

  const updateAgeFieldAndDob = (field: "year" | "month" | "day", value: string) => {
    const currentYear = formHook.getValues("year") || "0";
    const currentMonth = formHook.getValues("month") || "0";
    const currentDay = formHook.getValues("day") || "0";

    const nextYear = field === "year" ? value : currentYear;
    const nextMonth = field === "month" ? value : currentMonth;
    const nextDay = field === "day" ? value : field === "year" && !parseInt(currentDay, 10) ? "7" : currentDay;

    formHook.setValue("year", nextYear);
    formHook.setValue("month", nextMonth);
    formHook.setValue("day", nextDay);

    const yearNumber = parseInt(nextYear || "0", 10);
    const monthNumber = parseInt(nextMonth || "0", 10);
    const dayNumber = parseInt(nextDay || "0", 10);
    const dob = convertYDMtoDOBformat(yearNumber, monthNumber, dayNumber);
    formHook.setValue("dob", dob);
  };

  const getPetBreedItems = (speciesId: string) => {
    switch (speciesId) {
      case "1":
        return dogBreedItems;
      case "2":
        return catBreedItems;
      case "15":
        return giantBreedItems;
      default:
        return dogBreedItems;
    }
  };

  return (
    <div className="text-xs pl-2">
      {!hideTitle && (
        <p className="text-left text-sm font-sans font-bold text-gray-800 mt-2">
          <span>Add Pet Information</span>
        </p>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 mt-0 gap-4 lg:gap-12 w-full">
        <div>
          <div className="w-full flex flex-col sm:flex-row sm:items-center mt-3 gap-1 sm:gap-3">
            <p className="text-xs font-medium text-black sm:w-1/4">
              Pet Name <sup className="text-red-500">*</sup>
            </p>

            <div className="w-full lg:w-3/4">
              <Input
                disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                className="text-xs w-full"
                placeholder="Pet name"
                fieldError={formHook.formState.errors.pet_name}
                register={formHook.register("pet_name", {
                  ...forOnlyStrictAlphabets.validations,
                  required: true,
                  maxLength: 20,
                })}
                errorMessages={[
                  forOnlyStrictAlphabets.errors,
                  { type: "required", message: "Pet name is required" },
                  { type: "maxLength", message: "Too long pet name" },
                ]}
              />
            </div>
          </div>
          {/* Select Pet Species and Breed */}
          <div className="w-full flex flex-col lg:flex-row lg:items-center mt-4 gap-1 lg:gap-3">

            <p className="text-xs font-medium text-black lg:w-1/4">
              Species & Breed <sup className="text-red-500">*</sup>
            </p>
            <div className="w-full lg:w-3/4 flex flex-col sm:flex-row gap-2">
              <div className="flex-1 min-w-0">
                <NonSearchableSelectMenu
                  disableSearch={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                  disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                  listBoxClassName="w-full sm:w-52"
                  showDropdownIcon={true}
                  selectItems={speciesItems}
                  placeholder="Species"
                  className="text-xs"
                  onSelectItem={(item) => {
                    if (item) {
                      setSelectedSpecies(item.id);
                      formHook.setValue("species", item.title);
                      formHook.setValue("pet_species_type_id", item.id);
                      formHook.setValue("breed", "");
                    }
                  }}
                  defaultSelected={speciesItems.filter((s) => s.id === (speciesIdWatch as any) ?? "-1")[0]}
                  errorMessages={[{ type: "required", message: "Species required." }]}
                  fieldError={formHook.formState.errors.pet_species_type_id}
                  register={formHook.register("pet_species_type_id", {
                    required: true,
                  })}
                />
              </div>
              <div className="flex-1 min-w-0">
                <SearchableSelectMenu
                  placeholder="Breed"
                  disableSearch={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                  disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                  showTooltips={true}
                  showDropdownIcon={true}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("breed", item.title);
                      formHook.setValue("pet_breed_id", parseInt(item.id));
                    }
                  }}
                  selectItems={getPetBreedItems(formHook.watch("pet_species_type_id"))}
                  defaultSelected={
                    getPetBreedItems(formHook.watch("pet_species_type_id")).filter(
                      (pbi) => parseInt(pbi.id) === breedIdWatch,
                    )[0]
                  }
                  errorMessages={[{ type: "required", message: "Breed required." }]}
                  fieldError={formHook.formState.errors.pet_breed_id}
                  register={formHook.register("pet_breed_id", { required: true })}
                />
              </div>
            </div>
          </div>
          {/* Specie selection ends here */}
          <div className="w-full flex flex-col lg:flex-row lg:items-center mt-4 gap-1 lg:gap-3">

           <p className="text-xs font-medium text-black lg:w-1/4">
              Gender <sup className="text-red-500">*</sup>
            </p>

            <div className="w-full lg:w-1/3">
              <NonSearchableSelectMenu
                selectItems={genderItems}
                placeholder="Gender"
                disableSearch={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                className="text-xs"
                showDropdownIcon={true}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("gender", item.title);
                  }
                }}
                errorMessages={[{ type: "required", message: "Gender is required." }]}
                defaultSelected={
                  genderItems.filter((gi) => {
                    const title = gi?.title ?? "";
                    const id = gi?.id ?? "";
                    return (
                      title?.toLowerCase() === (genderWatch?.toLowerCase() ?? "") ||
                      id?.toLocaleLowerCase() === (genderWatch?.toLowerCase() ?? "")
                    );
                  })[0]
                }
                fieldError={formHook.formState.errors.gender}
                register={formHook.register("gender", { required: true })}
              />
            </div>
          </div>

          <div className="w-full flex flex-col lg:flex-row lg:items-center mt-4 gap-1 lg:gap-3">

           <p className="text-xs font-medium text-black lg:w-1/4">
              Pet Status <sup className="text-red-500">*</sup>
            </p>

            <div className="w-full lg:w-3/4 flex flex-wrap gap-x-4 gap-y-2">
              <div className="flex justify-start items-center">
                <CheckBox
                  circular={true}
                  disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                  colorHash="#22c55e"
                  onCheckChange={() => {
                    formHook.setValue("is_pet_deceased", false);
                    formHook.setValue("is_pet_inactive", false);
                  }}
                  fieldError={formHook.formState.errors.is_pet_active}
                  register={formHook.register("is_pet_active")}
                  errorMessages={[]}
                />
                <span className="text-left text-gray-500">Active</span>
              </div>
              <div className="flex justify-start items-center">
                <CheckBox
                  circular={true}
                  colorHash="#3b82f6"
                  disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                  onCheckChange={() => {
                    formHook.setValue("is_pet_deceased", false);
                    formHook.setValue("is_pet_active", false);
                  }}
                  fieldError={formHook.formState.errors.is_pet_inactive}
                  register={formHook.register("is_pet_inactive")}
                  errorMessages={[]}
                />
                <span className="text-left text-gray-500">InActive</span>
              </div>
              <div className="flex justify-start items-center">
                <CheckBox
                  circular={true}
                  colorHash="#dc2626"
                  disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                  onCheckChange={() => {
                    formHook.setValue("is_pet_active", false);
                    formHook.setValue("is_pet_inactive", false);
                  }}
                  fieldError={formHook.formState.errors.is_pet_deceased}
                  register={formHook.register("is_pet_deceased")}
                  errorMessages={[]}
                />
                <span className="text-left text-gray-500">Deceased</span>
              </div>
            </div>
          </div>
        </div>
        {/* Right block */}
        <div>
          <div className="w-full mt-3">
            <div className="w-full">
              <div className="grid grid-cols-1 md:grid-cols-[140px,1fr] gap-2 md:gap-3 items-start md:items-center w-full">
                <p className="font-medium text-black text-xs md:whitespace-nowrap">
                  DOB <sup className="text-red-500">*</sup>
                </p>

                <div className="w-full flex justify-between gap-3">
                  <div className="w-full">
                    <PWLCalendarComponent
                      disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                      showLeftNav={false}
                      allowPastDaySelection={true}
                      errorMessages={[{ type: "required", message: "Please select valid DOB" }]}
                      register={formHook.register("dob", {
                        required: true,
                      })}
                      showTypedErrors
                      disableDatesAfterDate={getCurrentDate()}
                      defaultSelectedDate={formHook.watch("dob")}
                      disabledFutureDateYearMonthSelection
                      fieldError={formHook.formState.errors.dob}
                      onSelectDate={(date) => {
                        const dobString = convertToValidDateStringFormat(date);
                        formHook.setValue("dob", dobString);
                        const d = convertDOBToYMDFormat(dobString);
                        formHook.setValue("day", d.days);
                        formHook.setValue("month", d.months);
                        formHook.setValue("year", d.years);
                      }}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[140px,1fr] gap-2 md:gap-3 items-start w-full mt-2">
                <p className="font-medium text-black text-xs md:whitespace-nowrap pt-2">Or Age</p>
                <div className="w-full">
                  <div className="mt-2">
                    <AgeSelectMenu
                      disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                      dayFieldError={formHook.formState.errors.day}
                      yearFieldError={formHook.formState.errors.year}
                      yearRegister={formHook.register("year")}
                      monthRegister={formHook.register("month")}
                      dayRegister={formHook.register("day")}
                      defaultValues={{
                        day: dayWatch,
                        month: monthWatch,
                        year: yearWatch,
                      }}
                      onDaySelect={(day) => {
                        updateAgeFieldAndDob("day", day);
                      }}
                      onMonthSelect={(month) => {
                        updateAgeFieldAndDob("month", month);
                      }}
                      onYearSelect={(year) => {
                        updateAgeFieldAndDob("year", year);
                      }}
                      onDayInputBlur={(day) => {
                        updateAgeFieldAndDob("day", day);
                      }}
                      onMonthInputBlur={(month) => {
                        updateAgeFieldAndDob("month", month);
                      }}
                      onYearInputBlur={(year) => {
                        updateAgeFieldAndDob("year", year);
                      }}
                      monthFieldError={formHook.formState.errors.month}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[140px,1fr] gap-2 md:gap-3 items-start md:items-center w-full mt-4">

            <p className="font-medium text-black text-xs md:whitespace-nowrap">
              Weight <sup className="text-red-500">*</sup>
            </p>

            <div className="w-full flex flex-col sm:flex-row gap-2">
              <div className="flex-1 min-w-0">
                <Input
                  className="text-xs w-full"
                  maxLength={2}
                  disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                  placeholder="Enter KGs"
                  fieldError={formHook.formState.errors.weight_kg}
                  register={formHook.register("weight_kg", {
                    ...forOnlyNumber.validations,
                    required: true,
                    maxLength: 2,
                    validate: (value) => {
                      const weightKg = parseInt(value || "0", 10);
                      const weightGm = parseInt(formHook.getValues("weight_gm") || "0", 10);
                      return weightKg > 0 || weightGm > 0 || "Weight cannot be 0";
                    },
                    deps: ["weight_gm"],
                  })}
                  endIcon={"KG"}
                  errorMessages={[
                    forOnlyNumber.errors,
                    { type: "required", message: "Pet weight is required" },
                    { type: "maxLength", message: "Invalid weight" },
                    { type: "validate", message: "Weight cannot be 0" },
                  ]}
                />
              </div>

              <div className="flex-1 min-w-0">
                <Input
                  maxLength={3}
                  disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                  className="text-xs w-full"
                  placeholder="Enter Grams"
                  fieldError={formHook.formState.errors.weight_gm}
                  register={formHook.register("weight_gm", {
                    ...forOnlyNumber.validations,
                    maxLength: 3,
                  })}
                  endIcon="GM"
                  errorMessages={[forOnlyNumber.errors, { type: "maxLength", message: "Invalid Weight" }]}
                />
              </div>
            </div>
          </div>
         <div className="grid grid-cols-1 md:grid-cols-[140px,1fr] gap-2 md:gap-3 items-start md:items-center w-full mt-4">

            <p className="font-medium text-black text-xs md:whitespace-nowrap">Pet Mood</p>

            <div className="w-full sm:w-1/3">
              <NonSearchableSelectMenu
                disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                disableSearch={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                fieldError={formHook.formState.errors.pet_mood}
                register={formHook.register("pet_mood")}
                placeholder="Select Mood"
                errorMessages={[]}
                className="text-xs"
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("pet_mood", item.title);
                  }
                }}
                defaultSelected={petMoods.filter((pmi) => pmi.title === petMoodWatch)[0]}
                selectItems={petMoods}
                showDropdownIcon={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetInfoFormComponent;
